import { useState } from 'react'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, Button } from '@besiktning/ui'
import { Star, MessageSquare } from 'lucide-react'
import { garageApi, carCareApi, formatInTz } from '@besiktning/ui'
import { useAuthStore } from '../store/authStore'
import { t } from '../i18n'
import type { Review } from '@besiktning/ui'

function Stars({ rating, size = 16 }: { rating: number; size?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          style={{ width: size, height: size }}
          className={star <= Math.round(rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
        />
      ))}
    </div>
  )
}

function ReviewCard({
  review,
  onReply,
  isReplying,
}: {
  review: Review
  onReply: (id: number, reply: string) => Promise<unknown>
  isReplying: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(review.reply ?? '')

  const dateLabel = review.created_at
    ? formatInTz(review.created_at, { day: 'numeric', month: 'short', year: 'numeric' })
    : ''

  const submit = async () => {
    try {
      await onReply(review.id, draft.trim())
      setEditing(false)
    } catch {
      // Keep the editor open; the error is surfaced by the mutation's onError.
    }
  }

  return (
    <div className="border-b border-gray-100 last:border-0 px-5 py-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Stars rating={review.rating} />
          <p className="mt-1 text-sm font-medium text-gray-900">{review.user_name || t('reviews.anonymousCustomer')}</p>
        </div>
        <span className="text-xs text-gray-400 whitespace-nowrap">{dateLabel}</span>
      </div>

      {review.comment ? (
        <p className="mt-2 text-sm leading-relaxed text-gray-700">{review.comment}</p>
      ) : (
        <p className="mt-2 text-sm italic text-gray-400">{t('reviews.noComment')}</p>
      )}

      {/* Reply block */}
      {review.reply && !editing ? (
        <div className="mt-3 rounded-lg border-l-2 border-blue-200 bg-blue-50 p-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-blue-700">{t('reviews.yourReply')}</p>
            <button
              onClick={() => {
                setDraft(review.reply ?? '')
                setEditing(true)
              }}
              className="text-xs font-medium text-blue-600 hover:underline"
            >
              {t('reviews.edit')}
            </button>
          </div>
          <p className="mt-1 text-sm leading-relaxed text-gray-700">{review.reply}</p>
        </div>
      ) : editing ? (
        <div className="mt-3">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder={t('reviews.replyPlaceholder')}
            className="w-full rounded-lg border border-gray-200 p-3 text-sm text-gray-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          <div className="mt-2 flex items-center gap-2">
            <Button size="sm" onClick={submit} disabled={isReplying}>
              {isReplying ? t('reviews.saving') : t('reviews.saveReply')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={isReplying}>
              {t('reviews.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => {
            setDraft('')
            setEditing(true)
          }}
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline"
        >
          <MessageSquare className="h-4 w-4" />
          {t('reviews.reply')}
        </button>
      )}
    </div>
  )
}

export default function Reviews() {
  const { currentMembership } = useAuthStore()
  const isCarCare = currentMembership?.type === 'car_care'
  const queryClient = useQueryClient()

  const queryKey = ['reviews', isCarCare ? 'car_care' : 'garage']

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      (isCarCare ? carCareApi.getReviews({ page: pageParam }) : garageApi.getReviews({ page: pageParam })),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.has_more ? lastPage.current_page + 1 : undefined),
  })

  const replyMutation = useMutation({
    mutationFn: ({ id, reply }: { id: number; reply: string }) =>
      isCarCare ? carCareApi.replyToReview(id, reply) : garageApi.replyToReview(id, reply),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
    onError: () => {
      window.alert(t('reviews.errSaveReply'))
    },
  })

  const reviews = data?.pages.flatMap((page) => page.reviews) ?? []
  const averageRating = data?.pages[0]?.average_rating ?? 0
  const reviewCount = data?.pages[0]?.review_count ?? 0

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('reviews.title')}</h1>
        <p className="text-gray-600">{t('reviews.subtitle')}</p>
      </div>

      {/* Summary */}
      <Card className="mb-6">
        <CardContent className="flex items-center gap-6 py-5">
          <div className="text-center">
            <p className="text-4xl font-bold text-gray-900">
              {averageRating.toFixed(1)}
            </p>
            <div className="mt-1 flex justify-center">
              <Stars rating={averageRating} size={18} />
            </div>
          </div>
          <div className="h-12 w-px bg-gray-200" />
          <div>
            <p className="text-2xl font-semibold text-gray-900">{reviewCount}</p>
            <p className="text-sm text-gray-500">
              {reviewCount === 1 ? t('reviews.reviewSingular') : t('reviews.reviewPlural')}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Star className="h-12 w-12 mb-3 text-gray-200" />
              <p className="text-sm">{t('reviews.empty')}</p>
            </div>
          ) : (
            <>
              {reviews.map((review) => (
                <ReviewCard
                  key={review.id}
                  review={review}
                  isReplying={replyMutation.isPending && replyMutation.variables?.id === review.id}
                  onReply={(id, reply) => replyMutation.mutateAsync({ id, reply })}
                />
              ))}
              {hasNextPage ? (
                <div className="flex justify-center py-4">
                  <Button
                    variant="outline"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? t('reviews.loading') : t('reviews.showMore')}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
